import { useMemo } from "react";
import { CodeMirrorEditor } from "./CodeMirrorEditor";
import { graphql } from "cm6-graphql";
import { json } from "@codemirror/lang-json";
import { buildClientSchema, type IntrospectionQuery } from "graphql";
import { useSchemaStore, useServersStore } from "../store";

interface GraphQLEditorProps {
  value: string;
  onChange: (value: string) => void;
  mode: "graphql" | "json" | "text";
  placeholder?: string;
  onExecute?: () => void;
}

export function GraphQLEditor({ value, onChange, mode, placeholder, onExecute }: GraphQLEditorProps) {
  const activeServerId = useServersStore((s) => s.activeServerId);
  const schemaCache = useSchemaStore((s) => s.schemaCache);

  const extensions = useMemo(() => {
    if (mode === "json") {
      return [json()];
    }

    if (mode === "text") {
      return [];
    }

    // Try to build a GraphQL schema for autocomplete
    if (activeServerId) {
      const cached = schemaCache[activeServerId];
      if (cached) {
        try {
          const introspection = JSON.parse(cached.introspectionJson);
          const schemaData = introspection.data ?? introspection;
          const schema = buildClientSchema(schemaData as IntrospectionQuery);
          return [graphql(schema)];
        } catch {
          // Fall back to basic GraphQL mode without schema
        }
      }
    }

    return [graphql()];
  }, [mode, activeServerId, schemaCache]);

  return (
    <CodeMirrorEditor
      value={value}
      onChange={onChange}
      extensions={extensions}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(onExecute !== undefined ? { onExecute } : {})}
    />
  );
}
